import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import logo from '../logo.png';
import { db } from '../firebase';
import { trackShowFavorited, trackShowUnfavorited } from '../analytics';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import { EffectCoverflow } from 'swiper/modules';
import { Pagination } from 'swiper/modules';
import scheduleData from '../scheduleData'; // Re-add for upload
import DarkVeil from './DarkVeil';

// Accept currentShow as a prop for live updating
// If not provided, fallback to local calculation (for direct use)
// This allows the Now Playing section and schedule card to stay in sync
// Usage: <SchedulePage currentShow={currentShowFromApp} />
// (currentShowFromApp is calculated in App.tsx using the live metadata logic)

// Accept props
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SchedulePage = ({ currentShow: propCurrentShow }: { currentShow?: any }) => {
  const navigate = useNavigate();
  const { toggleFavoriteShow, isFavoriteShow } = useNotifications();
  const todayIndex = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;
  const [localScheduleData, setLocalScheduleData] = useState<any>(null);
  const dayData = localScheduleData ? localScheduleData[todayIndex] : null;
  const swiperRef = useRef<any>(null);
  const [editMode, setEditMode] = useState(false);
  const [editModal, setEditModal] = useState<{dayIdx: number, slotIdx: number} | null>(null);
  const [editForm, setEditForm] = useState({ artist: '', show: '', genre: '', time: '', image: '' });
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addDayIdx, setAddDayIdx] = useState<number | null>(null);
  const [activeDay, setActiveDay] = useState(todayIndex);
  const [currentShow, setCurrentShow] = useState<any>(null);

  useEffect(() => {
    const username = localStorage.getItem('chat_username') || '';
    console.log('Current username:', username);
    setUsername(username);
  }, []);

  // Firestore: Load and subscribe to schedule
  useEffect(() => {
    setLoading(true);
    setError('');
    console.log('Attempting to load schedule from Firestore...');
    
    try {
      const scheduleRef = doc(db, 'schedule', 'main');
      const unsub = onSnapshot(scheduleRef, 
        (docSnap) => {
          console.log('Received schedule update from Firestore');
          if (docSnap.exists()) {
            const data = docSnap.data();
            console.log('Schedule data received:', data);
            if (data && data.data) {
              setLocalScheduleData(data.data);
              setLoading(false);
              console.log('Schedule data loaded successfully');
            } else {
              console.error('Invalid schedule data format');
              setError('Invalid schedule data format');
              setLoading(false);
            }
          } else {
            console.error('No schedule found in Firestore, using local data');
            setLocalScheduleData(scheduleData);
            setLoading(false);
          }
        }, 
        (err) => {
          console.error('Error loading schedule from Firestore, using local data:', err);
          setLocalScheduleData(scheduleData);
          setLoading(false);
        }
      );
      
      return () => {
        console.log('Cleaning up schedule subscription');
        unsub();
      };
    } catch (error) {
      console.error('Error setting up schedule subscription, using local data:', error);
      setLocalScheduleData(scheduleData);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!localScheduleData) return;
    const todayIndex = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;
    const dayData = localScheduleData[todayIndex];
    if (!dayData) return;
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const foundShow = dayData.shows.find((slot: any) => {
      const [startTime, endTime] = slot.time.split(' - ');
      
      // Parse start time
      const startMatch = startTime.match(/(\d+)(?::(\d+))?(am|pm)/i);
      if (!startMatch) return false;
      let startHour = parseInt(startMatch[1]);
      const startMinute = startMatch[2] ? parseInt(startMatch[2]) : 0;
      const isStartPM = startMatch[3].toLowerCase() === 'pm';
      if (isStartPM && startHour !== 12) startHour += 12;
      if (!isStartPM && startHour === 12) startHour = 0;
      
      // Parse end time
      const endMatch = endTime.match(/(\d+)(?::(\d+))?(am|pm)/i);
      if (!endMatch) return false;
      let endHour = parseInt(endMatch[1]);
      const endMinute = endMatch[2] ? parseInt(endMatch[2]) : 0;
      const isEndPM = endMatch[3].toLowerCase() === 'pm';
      if (isEndPM && endHour !== 12) endHour += 12;
      if (!isEndPM && endHour === 12) endHour = 0;
      
      // Convert to minutes for comparison
      const currentTimeInMinutes = currentHour * 60 + currentMinute;
      const startTimeInMinutes = startHour * 60 + startMinute;
      const endTimeInMinutes = endHour * 60 + endMinute;
      
      // Handle overnight shows (e.g., 11pm - 2am)
      if (endTimeInMinutes < startTimeInMinutes) {
        // Show spans midnight, so check if current time is after start OR before end
        return currentTimeInMinutes >= startTimeInMinutes || currentTimeInMinutes < endTimeInMinutes;
      } else {
        // Normal case: check if current time is within range
        return currentTimeInMinutes >= startTimeInMinutes && currentTimeInMinutes < endTimeInMinutes;
      }
    });
    setCurrentShow(foundShow || null);
    console.log('Current show for Live Now:', foundShow);
  }, [localScheduleData]);

  // Save edits to Firestore
  const saveEdit = async () => {
    if (localScheduleData) {
      console.log('Attempting to save schedule edit...');
      const updated = JSON.parse(JSON.stringify(localScheduleData));
      
      // Format the image path if it's not already in the correct format or is blank/whitespace
      let imagePath = editForm.image && editForm.image.trim();
      if (!imagePath || !imagePath.startsWith('/images/shows/')) {
        // Special cases for combined shows
        if (editForm.artist.includes("Tony o'Leary / Reggae Richie")) {
          imagePath = '/images/shows/Reggae_Richie_400.jpg';
        } else {
          // Convert artist name to image filename format
          const artistName = editForm.artist
            .replace(/[^a-zA-Z0-9]/g, '_') // Replace special chars with underscore
            .replace(/_+/g, '_') // Replace multiple underscores with single
            .replace(/^_|_$/g, '') // Remove leading/trailing underscores
            .replace(/O_Leary/g, 'OLeary') // Special case for O'Leary
            .replace(/O_Brien/g, 'OBrien'); // Special case for O'Brien
          
          imagePath = `/images/shows/${artistName}_400.jpg`;
        }
        console.log('Generated image path:', imagePath);
      }
      
      const updatedShow = {
        ...editForm,
        image: imagePath
      };
      
      console.log('Updated show data:', updatedShow);

      if (editModal) {
        if (editModal.slotIdx === -1) {
          // Add new show
          updated[editModal.dayIdx].shows.push(updatedShow);
          console.log('Added new show');
        } else {
          // Edit existing
          updated[editModal.dayIdx].shows[editModal.slotIdx] = updatedShow;
          console.log('Updated show at index:', editModal.slotIdx);
        }
        // Sort shows by start time after add/edit
        updated[editModal.dayIdx].shows.sort((a: any, b: any) => {
          const getStartMinutes = (slot: any) => {
            const [start] = slot.time.split(' - ');
            const match = start.match(/(\d+)(?::(\d+))?(am|pm)/i);
            if (!match) return 0;
            let hour = parseInt(match[1]);
            const minute = match[2] ? parseInt(match[2]) : 0;
            const isPM = match[3].toLowerCase() === 'pm';
            if (isPM && hour !== 12) hour += 12;
            if (!isPM && hour === 12) hour = 0;
            return hour * 60 + minute;
          };
          return getStartMinutes(a) - getStartMinutes(b);
        });
      } else if (addDayIdx !== null) {
        // Add new (fallback)
        updated[addDayIdx].shows.push(updatedShow);
        console.log('Added new show (fallback)');
        // Sort shows by start time after add
        updated[addDayIdx].shows.sort((a: any, b: any) => {
          const getStartMinutes = (slot: any) => {
            const [start] = slot.time.split(' - ');
            const match = start.match(/(\d+)(?::(\d+))?(am|pm)/i);
            if (!match) return 0;
            let hour = parseInt(match[1]);
            const minute = match[2] ? parseInt(match[2]) : 0;
            const isPM = match[3].toLowerCase() === 'pm';
            if (isPM && hour !== 12) hour += 12;
            if (!isPM && hour === 12) hour = 0;
            return hour * 60 + minute;
          };
          return getStartMinutes(a) - getStartMinutes(b);
        });
      }
      
      closeEditModal();
      setLoading(true);
      setError('');
      try {
        console.log('Saving to Firestore...');
        await setDoc(doc(db, 'schedule', 'main'), { data: updated });
        // Keep Swiper on the same day after add/edit
        const dayIdx = editModal ? editModal.dayIdx : addDayIdx;
        if (typeof dayIdx === 'number' && swiperRef.current && swiperRef.current.slideTo) {
          swiperRef.current.slideTo(dayIdx);
          setActiveDay(dayIdx);
        }
        console.log('Schedule saved successfully');
      } catch (e) {
        console.error('Failed to save schedule:', e);
        setError('Failed to save schedule.');
      }
      setLoading(false);
    }
  };

  // Add deleteShow function
  const deleteShow = async (dayIdx: number, slotIdx: number) => {
    if (localScheduleData) {
      const updated = JSON.parse(JSON.stringify(localScheduleData));
      updated[dayIdx].shows.splice(slotIdx, 1);
      setLoading(true);
      setError('');
      try {
        await setDoc(doc(db, 'schedule', 'main'), { data: updated });
        // Keep Swiper on the same day after delete
        if (swiperRef.current && swiperRef.current.slideTo) {
          swiperRef.current.slideTo(dayIdx);
        }
        setActiveDay(dayIdx);
      } catch (e) {
        setError('Failed to delete show.');
      }
      setLoading(false);
    }
  };

  // Helper to check if a slot is the current show (by artist, show, and genre)
  const isCurrentShow = (slot: any, slotIdx: number) => {
    if (activeDay !== todayIndex) return false;
    if (!dayData) return false;
    // Find the first slot that matches the current time
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const matchIdx = dayData.shows.findIndex((s: any) => {
      const [startTime, endTime] = s.time.split(' - ');
      
      // Parse start time
      const startMatch = startTime.match(/(\d+)(?::(\d+))?(am|pm)/i);
      if (!startMatch) return false;
      let startHour = parseInt(startMatch[1]);
      const startMinute = startMatch[2] ? parseInt(startMatch[2]) : 0;
      const isStartPM = startMatch[3].toLowerCase() === 'pm';
      if (isStartPM && startHour !== 12) startHour += 12;
      if (!isStartPM && startHour === 12) startHour = 0;
      
      // Parse end time
      const endMatch = endTime.match(/(\d+)(?::(\d+))?(am|pm)/i);
      if (!endMatch) return false;
      let endHour = parseInt(endMatch[1]);
      const endMinute = endMatch[2] ? parseInt(endMatch[2]) : 0;
      const isEndPM = endMatch[3].toLowerCase() === 'pm';
      if (isEndPM && endHour !== 12) endHour += 12;
      if (!isEndPM && endHour === 12) endHour = 0;
      
      // Convert to minutes for comparison
      const currentTimeInMinutes = currentHour * 60 + currentMinute;
      const startTimeInMinutes = startHour * 60 + startMinute;
      const endTimeInMinutes = endHour * 60 + endMinute;
      
      // Handle overnight shows (e.g., 11pm - 2am)
      if (endTimeInMinutes < startTimeInMinutes) {
        // Show spans midnight, so check if current time is after start OR before end
        return currentTimeInMinutes >= startTimeInMinutes || currentTimeInMinutes < endTimeInMinutes;
      } else {
        // Normal case: check if current time is within range
        return currentTimeInMinutes >= startTimeInMinutes && currentTimeInMinutes < endTimeInMinutes;
      }
    });
    return slotIdx === matchIdx;
  };

  // Open modal and populate form
  const openEditModal = (dayIdx: number, slotIdx: number) => {
    const slot = localScheduleData[dayIdx].shows[slotIdx];
    console.log('Opening edit modal for slot:', slot);
    
    // Try to find the correct image path for the artist
    let imagePath = slot.image;
    if (!imagePath || imagePath === '/images/shows/Play_Show_Replays_400.jpg') {
      // Special cases for combined shows
      if (slot.artist.includes("Tony o'Leary / Reggae Richie")) {
        imagePath = '/images/shows/Reggae_Richie_400.jpg';
      } else {
        const artistName = slot.artist
          .replace(/[^a-zA-Z0-9]/g, '_')
          .replace(/_+/g, '_')
          .replace(/^_|_$/g, '')
          .replace(/O_Leary/g, 'OLeary')
          .replace(/O_Brien/g, 'OBrien');
        imagePath = `/images/shows/${artistName}_400.jpg`;
      }
    }
    
    setEditForm({
      artist: slot.artist || '',
      show: slot.show || '',
      genre: slot.genre || '',
      time: slot.time || '',
      image: imagePath,
    });
    setEditModal({ dayIdx, slotIdx });
  };

  const closeEditModal = () => {
    setEditModal(null);
    setAddDayIdx(null);
    // Reset the form to empty state
    setEditForm({ artist: '', show: '', genre: '', time: '', image: '' });
  };

  const uploadStaticSchedule = async () => {
    try {
      if (!localScheduleData) {
        alert('No schedule data to upload');
        return;
      }
      await setDoc(doc(db, 'schedule', 'main'), { data: localScheduleData });
      alert('Schedule uploaded to Firestore!');
    } catch (e) {
      alert('Failed to upload schedule: ' + e);
    }
  };

  // Add this useEffect to maintain the current day view
  useEffect(() => {
    if (swiperRef.current && swiperRef.current.slideTo) {
      swiperRef.current.slideTo(activeDay);
    }
  }, [activeDay]);

  if (loading) {
    return <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white">Loading schedule...</div>;
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white">
        {error}
        {username === 'Karll' && (
          <button
            className="mt-6 bg-brand-pink text-white px-4 py-2 rounded-lg"
            onClick={uploadStaticSchedule}
          >
            Upload
          </button>
        )}
      </div>
    );
  }
  if (!localScheduleData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white">
        No schedule data available.
        {username === 'Karll' && (
          <button
            className="mt-6 bg-brand-pink text-white px-4 py-2 rounded-lg"
            onClick={uploadStaticSchedule}
          >
            Upload
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="schedule-page bg-black min-h-screen text-white flex flex-col w-full max-w-full px-4 pt-12 pb-8 relative" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      <div className="absolute inset-0 z-0">
        <DarkVeil hueShift={264} />
      </div>
      <div className="relative z-10">
        {/* Header */}
      <header className="z-50" style={{ paddingTop: 'env(safe-area-inset-top, 0px)', marginBottom: '2px' }}>
        {/* Admin controls row (only for Karll) */}
        {username === 'Karll' && (
          <div className="flex items-center gap-3 px-4 sm:px-6 pt-2 pb-2 w-full max-w-full">
            <button
              className="flex-1 bg-white/10 hover:bg-white/20 transition-colors px-2 py-2 rounded-lg text-white font-medium text-sm text-center"
              onClick={() => setEditMode((v) => !v)}
            >
              {editMode ? 'Done' : 'Manage'}
            </button>
            <button
              className="flex-1 bg-white/10 hover:bg-white/20 transition-colors px-2 py-2 rounded-lg text-white font-medium text-sm text-center"
              onClick={uploadStaticSchedule}
            >
              Upload
            </button>
            <button
              className="flex-1 text-white/80 hover:text-white transition-colors text-lg text-center px-2 py-2"
              onClick={() => navigate('/')}
            >
              Close
            </button>
          </div>
        )}
        {/* Heading row */}
        <div className={`flex items-center ${username === 'Karll' ? 'justify-start' : 'justify-between'} px-4 sm:px-6 py-0 w-full max-w-full pb-4`}>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">Today's Schedule</h1>
          {username !== 'Karll' && (
            <div className="flex items-center gap-3">
              <button className="text-white/80 hover:text-white transition-colors text-lg" onClick={() => navigate('/')}>Close</button>
            </div>
          )}
        </div>
        {/* Now Playing section inside header */}
        {currentShow && (
          <div className="px-4 sm:px-6 pb-1 w-full max-w-full">
            <div className="bg-white/20 rounded-[2rem] p-4 border border-white/10">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full overflow-hidden ring-2 ring-white/20 flex-shrink-0">
                  <img 
                    src={currentShow.image || logo} 
                    alt={currentShow.artist} 
                    className="w-full h-full object-cover"
                    onError={e => (e.currentTarget.src = logo)} 
                  />
                </div>
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                  {currentShow.artist === 'Play Show Replays' ? (
                    <span className="text-brand-pink font-semibold text-base mb-1">Now Playing</span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-white text-[0.7rem] font-semibold text-brand-pink gap-1 mb-1 w-fit" style={{ borderWidth: '1px' }}>
                      <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" style={{ display: 'inline-block' }}></span>
                      On Air
                    </span>
                  )}
                  <span className="text-white font-semibold text-lg leading-tight truncate">{currentShow.artist}</span>
                  {currentShow.show && (
                    <span className="text-white/80 text-base font-normal truncate">{currentShow.show}</span>
                  )}
                  {currentShow.genre && (
                    <span className="text-brand-pink text-sm font-medium mt-0.5 truncate">{currentShow.genre}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>
      {/* Edit Modal */}
      {editModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 w-full max-w-md relative">
            <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={closeEditModal}>✕</button>
            <h2 className="text-xl font-bold text-white mb-4">Edit Show Slot</h2>
            <form onSubmit={e => { e.preventDefault(); saveEdit(); }} className="space-y-4">
              <div>
                <label className="block text-white/80 mb-1">Artist</label>
                <input className="w-full rounded-lg px-3 py-2 bg-white/10 text-white" value={editForm.artist} onChange={e => setEditForm(f => ({...f, artist: e.target.value}))} />
              </div>
              <div>
                <label className="block text-white/80 mb-1">Show</label>
                <input className="w-full rounded-lg px-3 py-2 bg-white/10 text-white" value={editForm.show} onChange={e => setEditForm(f => ({...f, show: e.target.value}))} />
              </div>
              <div>
                <label className="block text-white/80 mb-1">Genre</label>
                <input className="w-full rounded-lg px-3 py-2 bg-white/10 text-white" value={editForm.genre} onChange={e => setEditForm(f => ({...f, genre: e.target.value}))} />
              </div>
              <div>
                <label className="block text-white/80 mb-1">Time</label>
                <input className="w-full rounded-lg px-3 py-2 bg-white/10 text-white" value={editForm.time} onChange={e => setEditForm(f => ({...f, time: e.target.value}))} />
              </div>
              <div>
                <label className="block text-white/80 mb-1">Image URL</label>
                <input className="w-full rounded-lg px-3 py-2 bg-white/10 text-white" value={editForm.image} onChange={e => setEditForm(f => ({...f, image: e.target.value}))} />
              </div>
              <div className="flex gap-2 mt-4">
                <button type="button" className="flex-1 py-2 rounded-lg bg-white/20 text-white hover:bg-white/30" onClick={closeEditModal}>Cancel</button>
                <button type="submit" className="flex-1 py-2 rounded-lg bg-brand-pink text-white hover:bg-brand-pink/80">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Swipeable schedule list card for each day */}
      <div className="px-2 sm:px-6 mt-1 pb-20 flex flex-col items-center w-full max-w-full">
        {/* Swipe indicator (mobile only) */}
        <div className="flex flex-col items-center sm:hidden py-3">
          <div className="flex items-center gap-1 text-white/60 text-xs">
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="chevron-left" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"></polyline></svg>
            <span>Swipe for more</span>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="chevron-right" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </div>
        </div>
        <div className="w-full">
          <Swiper
            ref={swiperRef}
            effect="coverflow"
            grabCursor={true}
            centeredSlides={true}
            slidesPerView="auto"
            speed={800}
            spaceBetween={30}
            keyboard={{
              enabled: true,
              onlyInViewport: true,
            }}
            mousewheel={{
              forceToAxis: true,
              sensitivity: 1,
            }}
            coverflowEffect={{
              rotate: 30,
              stretch: 0,
              depth: 150,
              modifier: 1.5,
              slideShadows: true,
            }}
            pagination={{
              clickable: true,
              dynamicBullets: true,
            }}
            modules={[EffectCoverflow, Pagination]}
            className="w-full"
            style={{ height: 'auto' }}
            initialSlide={activeDay}
            onSlideChange={(swiper) => setActiveDay(swiper.activeIndex)}
            touchStartPreventDefault={false}
            touchMoveStopPropagation={false}
            resistance={true}
            resistanceRatio={0.85}
            watchSlidesProgress={true}
            observer={true}
            observeParents={true}
          >
            {localScheduleData.map((day: any, idx: number) => (
              <SwiperSlide key={day.day} style={{ height: 'auto' }}>
                {({ isActive }) => (
                  <div className="flex flex-col items-center w-full h-auto">
                    <div
                      className="w-full max-w-md bg-white/20 rounded-[2rem] p-4 border border-white/10 hide-scrollbar h-auto"
                      style={{ marginBottom: '40px', paddingBottom: '0px' }}
                    >
                      <div className="text-white font-bold text-xl mb-4 pl-1">{day.day}</div>
                      {day.shows.map((slot: any, i: number) => (
                        <div key={i} className={`relative flex items-center gap-4 py-4 ${i !== 0 ? 'border-t border-white/10' : ''}`} style={{ minHeight: '80px' }}>
                          {/* Artwork */}
                          <div className="w-20 h-20 rounded-full overflow-hidden ring-1 ring-white/10 flex-shrink-0">
                            <img
                              src={slot.image || logo}
                              alt={slot.artist}
                              className="w-full h-full object-cover"
                              onError={e => (e.currentTarget.src = logo)}
                            />
                          </div>
                          {/* Show info */}
                          <div className="flex-1 min-w-0 flex flex-col justify-center">
                            {/* Time above artist, branded pink */}
                            <div className="text-brand-pink text-xs font-bold leading-tight mb-0.5">{slot.time}</div>
                            <div className="text-white font-semibold text-base leading-tight truncate">{slot.artist}</div>
                            {slot.show && (
                              <div className="text-white/80 text-sm font-normal truncate">{slot.show}</div>
                            )}
                            <div className="text-brand-pink text-xs font-medium mt-0.5 truncate">{slot.genre}</div>
                            {/* On Air badge below genre if needed */}
                            {idx === todayIndex && isCurrentShow(slot, i) && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-white text-[0.7rem] font-semibold text-brand-pink gap-1 mt-1 w-fit" style={{ borderWidth: '1px' }}>
                                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" style={{ display: 'inline-block' }}></span>
                                On Air
                              </span>
                            )}
                          </div>
                          {/* Favorite button */}
                          <div className="flex items-center">
                            {(() => {
                              const showId = `${slot.artist}-${slot.show || 'Show'}`;
                              const isFavorite = isFavoriteShow(showId);
                              return (
                                <button
                                  className="p-2 rounded-lg transition-colors"
                                  onClick={() => toggleFavoriteShow(showId)}
                                  aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                                >
                                  <Heart className={`w-5 h-5 transition-colors ${isFavorite ? 'text-brand-pink fill-brand-pink' : 'text-white/60'}`} />
                                </button>
                              );
                            })()}
                          </div>
                          {/* Edit/Delete buttons in edit mode */}
                          {editMode && (
                            <div className="absolute top-4 right-4 z-20 flex gap-2">
                              <button
                                className="bg-brand-pink/80 hover:bg-brand-pink text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg transition-colors"
                                onClick={() => { openEditModal(idx, i); }}
                              >
                                Edit
                              </button>
                              <button
                                className="bg-red-600/80 hover:bg-red-600 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg transition-colors"
                                onClick={() => deleteShow(idx, i)}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      {/* Add New Show button in edit mode for admin */}
                      {editMode && username === 'Karll' && (
                        <div className="flex justify-center mt-4">
                          <button
                            className="bg-brand-pink/80 hover:bg-brand-pink text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg transition-colors"
                            onClick={() => { 
                              setEditForm({ artist: '', show: '', genre: '', time: '', image: '' });
                              setEditModal({ dayIdx: idx, slotIdx: -1 }); 
                              setAddDayIdx(idx); 
                            }}
                          >
                            Add New Show
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
      </div>
    </div>
  );
};

export default SchedulePage; 